import { useEffect, useState } from "react";

import { DateTime } from "luxon";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import Modal from "../Shared/Modal";
import SwitchInput from "../Shared/Inputs/SwitchInput";
import { TextInput } from "../Shared/Inputs/TextInput";
import { ButtonInput } from "../Shared/Inputs/ButtonInput";
import useSocialSchedules from "../../data/useSocialSchedules";
import ConfigureTriggerOptions from "./ConfigureTriggerOptions";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { createReverseLookup, socialTriggerType } from "../../constants/enums";

const reverseLookup = createReverseLookup(socialTriggerType);

export default function AddEditScheduleModal({
  open,
  setOpen,
  defaultTriggerType = "",
  schedule = null,
}) {
  const { agencyId, pageId } = useParams();
  const { socialSchedules, add, update } = useSocialSchedules(agencyId);
  const { saveHandlers } = useDefaultCRUDHandlers("Social Schedule");
  const [error, setError] = useState(null);
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      isActive: schedule ? schedule.isActive : true,
      name: schedule ? schedule.name : `New ${defaultTriggerType} Schedule`,
      trigger: {
        triggerType: defaultTriggerType ? reverseLookup[defaultTriggerType] : 0, //Defaults to schedule if none found
        interval: 0,
        startDateTime: null,
        endDateTime: null,
        weekDays: [],
        monthDays: [],
        branches: "",
        minPriceSales: 0,
        minPriceLettings: 0,
        isSales: false,
        isLettings: false,
        postOnReduced: false,
        postOnCompleted: false,
        postOnNew: false,
        minStars: 4,
        allowContentEngine: false,
        allowNonContentEngine: false,
        propertyMedia: "1",
        numberOfImages: "1",
        asLinkPost: true,
      },
    },
  });

  const triggerTypeValue = watch("trigger.triggerType");

  // Whenever `schedule` changes, translate its values into the form and reset
  useEffect(() => {
    if (!schedule) return;

    const sched = { ...schedule };
    const trig = {
      ...schedule.trigger,
      propertyMedia: schedule.trigger?.propertyMedia?.toString(),
      numberOfImages: schedule.trigger?.numberOfImages?.toString(),
    };

    // If there is a startDateTime/endDateTime, convert to input-friendly format
    if (trig.startDateTime) {
      trig.startDateTime = DateTime.fromISO(trig.startDateTime).toFormat(
        "yyyy-MM-dd'T'HH:mm"
      );
    }
    if (trig.endDateTime) {
      trig.endDateTime = DateTime.fromISO(trig.endDateTime).toFormat(
        "yyyy-MM-dd'T'HH:mm"
      );
    }

    // Convert weekDays and monthDays from CSV strings into arrays
    if (trig.weekDays) {
      trig.weekDays = trig.weekDays.split(",");
    }
    if (trig.monthDays) {
      trig.monthDays = trig.monthDays.split(",");
    }

    // For properties‐type triggers, `branches` is already a string; leave as is
    // For reviews‐type triggers, `minStars` is already a number; leave as is
    // For properties, also cast any missing numeric fields to defaults
    // trig.branches = trig.branches ?? "";
    // trig.minPriceSales = trig.minPriceSales ?? 0;
    // trig.minPriceLettings = trig.minPriceLettings ?? 0;
    // trig.isSales = trig.isSales ?? false;
    // trig.isLettings = trig.isLettings ?? false;
    // trig.postOnReduced = trig.postOnReduced ?? false;
    // trig.postOnCompleted = trig.postOnCompleted ?? false;
    // trig.postOnNew = trig.postOnNew ?? false;
    // trig.minStars = trig.minStars ?? 4;

    sched.trigger = trig;

    reset({
      isActive: sched.isActive,
      name: sched.name,
      trigger: sched.trigger,
    });
  }, [schedule, reset]);

  const onSubmit = (data) => {
    // console.log(data);
    setError(null);

    const type = parseInt(data.trigger.triggerType);

    // Validation for a scheduled trigger
    if (
      type == reverseLookup["Schedule"] ||
      type == reverseLookup["Evergreen"] ||
      type == reverseLookup["Infographic"]
    ) {
      if (!data.trigger.startDateTime) {
        setError("Please select a start date and time.");
        return;
      }
      if (
        data.trigger.interval === null ||
        data.trigger.interval === "" ||
        data.trigger.interval === undefined ||
        data.trigger.interval < 0
      ) {
        setError("Please select a valid interval.");
        return;
      }
    }

    // Validation for a reviews‐type trigger
    if (type == reverseLookup["Reviews"]) {
      const ms = data.trigger.minStars;
      if (!ms || ms < 1 || ms > 5) {
        setError("Please select a valid minimum star rating (1-5).");
        return;
      }
    }

    let body = {
      ...data,
      trigger: {
        ...data.trigger,
        numberOfImages: parseInt(data.trigger.numberOfImages) || 0,
        propertyMedia: parseInt(data.trigger.propertyMedia) || 0,
      },
    };

    let mutation = add;

    if (schedule) {
      body = {
        ...schedule,
        ...data,
        trigger: {
          ...schedule.trigger,
          ...data.trigger,
          numberOfImages: parseInt(data.trigger.numberOfImages) || 0,
          propertyMedia: parseInt(data.trigger.propertyMedia) || 0,
        },
      };

      mutation = update;
    }

    body.metaPageConfigId = pageId;
    body.agencyId = agencyId;
    body.branchId = null;

    // Coerce triggerType to number
    body.trigger.triggerType = type;

    // Convert arrays back to strings if present
    if (data.trigger.weekDays) {
      body.trigger.weekDays = data.trigger.weekDays.toString();
    } else {
      body.trigger.weekDays = null;
    }
    if (data.trigger.monthDays) {
      body.trigger.monthDays = data.trigger.monthDays.toString();
    } else {
      body.trigger.monthDays = null;
    }

    console.log(body);

    //add loading state and close modal
    mutation.mutate(body, {
      onSuccess: (data) => {
        saveHandlers.onSuccess();
        reset();
        setOpen(false);
      },
      onError: (error) => {
        saveHandlers.onError();
      },
    });
  };

  useEffect(() => {
    setError(null);
    const current = watch("trigger.triggerType");
    const desired = reverseLookup[defaultTriggerType] ?? 0;

    // If the current trigger type is undefined, null, or not equal to the desired one, set it
    // Helps when changing the trigger type when going to different sections
    if (
      current === undefined ||
      current === null ||
      parseInt(current, 10) !== desired
    ) {
      setValue("trigger.triggerType", desired);
    }
  }, [defaultTriggerType, watch("trigger.triggerType"), setValue]);

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-6xl">
      <form onSubmit={handleSubmit(onSubmit)}>
        <p>New {defaultTriggerType} Social Schedule</p>
        <hr className="my-4" />
        <div className="grid grid-cols-2 gap-4">
          <TextInput label="Name" register={register} registerName={"name"} />
          <div className="flex items-center justify-end mt-4">
            <SwitchInput
              label="Active"
              register={register}
              watch={watch}
              setValue={setValue}
              registerName={"isActive"}
            />
          </div>
        </div>
        <hr className="my-4" />
        <ConfigureTriggerOptions
          triggerType={triggerTypeValue}
          register={register}
          setValue={setValue}
          getValues={getValues}
          watch={watch}
          errors={errors}
          control={control}
        />

        <ButtonInput
          label={schedule ? "Update" : "Create"}
          isSubmit={true}
          classes={"mt-4"}
        />
        <div className="font-semibold text-red-600 text-sm mt-2">{error}</div>
      </form>
    </Modal>
  );
}
